import { useState } from 'react';
import { useReveal } from '@/hooks/useReveal';

const plans = [
  {
    id: 'bulk-messaging',
    title: 'WhatsApp Bulk Messaging',
    badge: 'Starter Pack',
    description: 'Reach thousands of customers instantly with powerful WhatsApp bulk messaging.',
    monthlyPrice: '₹1,499',
    monthlyLabel: '/ Month',
    yearlyPrice: '₹14,999',
    yearlyLabel: '/ Year',
    monthlyEquivalent: '₹1,250',
    yearlyEquivalent: '/ Month',
    savingsPercent: 17,
    features: [
      'Unlimited Campaign Creation',
      'Image, PDF & Video Messages',
      'Personalized Messaging',
      'Contact Management',
      'Campaign Scheduling',
      'Delivery Reports',
      'WhatsApp Support',
      'Beginner Friendly',
    ],
    ctaLabel: 'Start Now',
    ctaHref: 'https://automate.dgflow.in/signup',
    secondaryLabel: 'Talk to an Expert',
    secondaryHref: 'https://wa.me/919746765488?text=Hi,%0A%0AI%20want%20to%20know%20more%20about%20your%20services.',
    accentBorder: 'primary',
    popular: false,
  },
  {
    id: 'automation',
    title: 'WhatsApp Automation',
    badge: 'Pro Pack',
    description: 'Automate replies, follow-ups and customer engagement 24×7.',
    monthlyPrice: '₹3,499',
    monthlyLabel: '/ Month',
    yearlyPrice: '₹29,999',
    yearlyLabel: '/ Year',
    monthlyEquivalent: '₹2,500',
    yearlyEquivalent: '/ Month',
    savingsPercent: 29,
    features: [
      'Auto Replies',
      'Lead Capture',
      'Customer Follow-up',
      'Workflow Automation',
      'Appointment Reminders',
      'AI Ready',
      'CRM Integration',
      'Multi-step Automation',
    ],
    ctaLabel: 'Start Now',
    ctaHref: 'https://automate.dgflow.in/signup',
    secondaryLabel: 'Book Demo',
    secondaryHref: '#demo-form',
    accentBorder: 'primaryLight',
    popular: true,
  },
];

const trustItems = [
  {
    icon: 'ri-rocket-2-line',
    title: 'Quick Setup',
    description: 'Get started in under 10 minutes with our guided onboarding.',
  },
  {
    icon: 'ri-shield-check-line',
    title: 'Secure Platform',
    description: 'End-to-end encryption with enterprise-grade security standards.',
  },
  {
    icon: 'ri-chat-smile-2-line',
    title: 'Expert Support',
    description: 'Dedicated WhatsApp support from real automation specialists.',
  },
  {
    icon: 'ri-line-chart-line',
    title: 'Business Growth',
    description: 'Our clients see an average 5x ROI within the first 90 days.',
  },
];

export default function PricingSection() {
  const { ref, visible } = useReveal();
  const [hoveredCard, setHoveredCard] = useState<string | null>(null);
  const [isYearly, setIsYearly] = useState(false);

  return (
    <section id="pricing" className="relative py-20 md:py-28 bg-background-50 overflow-hidden">
      {/* Soft decorative blobs */}
      <div className="absolute top-40 -left-32 w-[500px] h-[500px] rounded-full bg-primary-500/4 blur-3xl pointer-events-none"></div>
      <div className="absolute bottom-40 -right-32 w-[500px] h-[500px] rounded-full bg-primary-400/4 blur-3xl pointer-events-none"></div>
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[400px] rounded-full bg-primary-500/2 blur-3xl pointer-events-none"></div>

      {/* Subtle grid pattern */}
      <div
        className="absolute inset-0 opacity-[0.015] pointer-events-none"
        style={{ backgroundImage: 'radial-gradient(circle, oklch(var(--foreground-600)) 1px, transparent 1px)', backgroundSize: '40px 40px' }}
      ></div>

      <div ref={ref} className={`max-w-7xl mx-auto px-4 md:px-6 reveal ${visible ? 'visible' : ''}`}>
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-10 md:mb-14">
          <span className="inline-block px-4 py-1.5 rounded-full bg-primary-100 text-primary-700 text-xs font-semibold uppercase tracking-wider mb-5">
            Pricing Plans
          </span>
          <h2 className="text-3xl md:text-5xl font-bold font-heading text-foreground-950 mb-4 leading-tight">
            Choose the Perfect Plan{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-500 to-primary-400">
              for Your Business
            </span>
          </h2>
          <p className="text-base md:text-lg text-foreground-600 leading-relaxed max-w-2xl mx-auto">
            Start with our Pro Pack and automate your customer communication. Upgrade anytime as your business grows.
          </p>

          {/* Billing Toggle */}
          <div className="mt-8 flex items-center justify-center">
            <div className="inline-flex items-center gap-2 p-1 rounded-full bg-background-100 border border-background-200/70">
              <button
                onClick={() => setIsYearly(false)}
                className={`relative px-5 py-2 rounded-full text-sm font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer ${
                  !isYearly
                    ? 'bg-primary-500 text-white shadow-[0_2px_8px_rgba(30,94,255,0.2)]'
                    : 'text-foreground-600 hover:text-foreground-900'
                }`}
              >
                Monthly
              </button>
              <button
                onClick={() => setIsYearly(true)}
                className={`relative px-5 py-2 rounded-full text-sm font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer flex items-center gap-2 ${
                  isYearly
                    ? 'bg-primary-500 text-white shadow-[0_2px_8px_rgba(30,94,255,0.2)]'
                    : 'text-foreground-600 hover:text-foreground-900'
                }`}
              >
                Yearly
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold tracking-wide transition-colors duration-300 ${
                  isYearly
                    ? 'bg-white/20 text-white'
                    : 'bg-primary-100 text-primary-700'
                }`}>
                  Save 17-29%
                </span>
              </button>
            </div>
          </div>
        </div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-10 max-w-5xl mx-auto mb-16 md:mb-20">
          {plans.map(plan => (
            <div
              key={plan.id}
              onMouseEnter={() => setHoveredCard(plan.id)}
              onMouseLeave={() => setHoveredCard(null)}
              className={`relative group bg-background-50/80 backdrop-blur-xl border rounded-[20px] p-8 md:p-10 flex flex-col transition-all duration-500 overflow-hidden ${
                hoveredCard === plan.id
                  ? plan.accentBorder === 'primary'
                    ? 'border-primary-300/50 shadow-[0_8px_40px_-12px_rgba(30,94,255,0.15)] -translate-y-1.5'
                    : 'border-primary-300/40 shadow-[0_8px_40px_-12px_rgba(79,163,255,0.15)] -translate-y-1.5'
                  : plan.popular
                    ? 'border-primary-200/50 shadow-[0_4px_24px_-8px_rgba(30,94,255,0.1)]'
                    : 'border-background-200/70 shadow-[0_2px_16px_-6px_rgba(0,0,0,0.04)]'
              }`}
            >
              {/* Most Popular Ribbon */}
              {plan.popular && (
                <div className="absolute top-0 right-0 z-20">
                  <div className="relative">
                    <div className="absolute top-0 right-0 w-36 h-36 overflow-hidden">
                      <div className="absolute top-[18px] -right-[42px] w-[170px] bg-gradient-to-r from-primary-500 to-primary-400 text-white text-[11px] font-bold uppercase tracking-widest text-center py-1.5 rotate-45 shadow-[0_2px_8px_rgba(30,94,255,0.2)]">
                        Most Popular
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Glass shimmer on hover */}
              <div
                className={`absolute inset-0 rounded-[20px] bg-gradient-to-br from-primary-500/3 via-transparent to-primary-400/3 opacity-0 transition-opacity duration-500 pointer-events-none ${
                  hoveredCard === plan.id ? 'opacity-100' : ''
                }`}
              ></div>

              <div className="relative z-10 flex flex-col flex-1">
                {/* Badge */}
                <div className="mb-5">
                  <span className={`inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs font-bold tracking-wide uppercase bg-gradient-to-r from-primary-500 to-primary-400 text-white shadow-[0_2px_8px_rgba(30,94,255,0.25)]`}>
                    <span className="w-1.5 h-1.5 rounded-full bg-white/80"></span>
                    {plan.badge}
                  </span>
                </div>

                {/* Title & Description */}
                <h3 className="text-xl md:text-2xl font-bold font-heading text-foreground-950 mb-2">
                  {plan.title}
                </h3>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-8">
                  {plan.description}
                </p>

                {/* Pricing - Dynamic based on billing toggle */}
                <div className="mb-8">
                  {!isYearly ? (
                    <>
                      {/* Monthly Active */}
                      <div className="flex items-baseline gap-1.5 mb-3">
                        <span className="text-4xl md:text-5xl font-extrabold font-heading text-foreground-950 tracking-tight">
                          {plan.monthlyPrice}
                        </span>
                        <span className="text-sm md:text-base font-medium text-foreground-500">
                          {plan.monthlyLabel}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-px bg-background-200/70"></div>
                        <div className="flex items-baseline gap-1.5 px-4 py-2 rounded-full bg-background-100 border border-background-200/50">
                          <span className="text-sm font-bold font-heading text-foreground-900">
                            {plan.yearlyPrice}
                          </span>
                          <span className="text-xs font-medium text-foreground-500">
                            {plan.yearlyLabel}
                          </span>
                        </div>
                        <div className="flex-1 h-px bg-background-200/70"></div>
                      </div>
                      <p className="text-xs text-foreground-400 mt-2 text-center">
                        Save ~{plan.savingsPercent}% with yearly billing
                      </p>
                    </>
                  ) : (
                    <>
                      {/* Yearly Active */}
                      <div className="flex items-baseline gap-1.5 mb-1">
                        <span className="text-4xl md:text-5xl font-extrabold font-heading text-foreground-950 tracking-tight">
                          {plan.yearlyPrice}
                        </span>
                        <span className="text-sm md:text-base font-medium text-foreground-500">
                          {plan.yearlyLabel}
                        </span>
                      </div>
                      <div className="flex items-baseline gap-2 mb-3">
                        <span className="text-sm text-foreground-400 line-through">
                          {plan.monthlyPrice}
                          {plan.monthlyLabel}
                        </span>
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-green-50 border border-green-200/60 text-green-700 text-[11px] font-bold">
                          Save {plan.savingsPercent}%
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-px bg-background-200/70"></div>
                        <div className="flex items-baseline gap-1.5 px-4 py-2 rounded-full bg-primary-50 border border-primary-200/50">
                          <span className="text-sm font-bold font-heading text-primary-700">
                            {plan.monthlyEquivalent}
                          </span>
                          <span className="text-xs font-medium text-primary-500">
                            {plan.yearlyEquivalent}
                          </span>
                        </div>
                        <div className="flex-1 h-px bg-background-200/70"></div>
                      </div>
                      <p className="text-xs text-foreground-400 mt-2 text-center">
                        Billed annually — equivalent to {plan.monthlyEquivalent}/month
                      </p>
                    </>
                  )}
                </div>

                {/* Feature List */}
                <div className="space-y-3 mb-8 flex-1">
                  {plan.features.map(feature => (
                    <div key={feature} className="flex items-start gap-3">
                      <span className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${
                        plan.accentBorder === 'primary' ? 'bg-primary-100' : 'bg-primary-50'
                      }`}>
                        <i className={`ri-check-line text-[10px] font-bold ${
                          plan.accentBorder === 'primary' ? 'text-primary-600' : 'text-primary-400'
                        }`}></i>
                      </span>
                      <span className="text-sm text-foreground-700 leading-relaxed">{feature}</span>
                    </div>
                  ))}
                </div>

                {/* CTA Buttons */}
                <div className="space-y-3">
                  <a
                    href={plan.ctaHref}
                    target={plan.ctaHref.startsWith('http') ? '_blank' : undefined}
                    rel={plan.ctaHref.startsWith('http') ? 'noopener noreferrer' : undefined}
                    className={`block w-full text-center px-6 py-3.5 rounded-full text-sm font-bold transition-all duration-300 whitespace-nowrap cursor-pointer ${
                      plan.accentBorder === 'primary'
                        ? 'bg-primary-500 text-white hover:bg-primary-600 hover:scale-[1.02] active:scale-[0.98] shadow-[0_4px_16px_rgba(30,94,255,0.25)] hover:shadow-[0_6px_24px_rgba(30,94,255,0.35)]'
                        : 'bg-primary-400 text-white hover:bg-primary-500 hover:scale-[1.02] active:scale-[0.98] shadow-[0_4px_16px_rgba(79,163,255,0.25)] hover:shadow-[0_6px_24px_rgba(79,163,255,0.35)]'
                    }`}
                  >
                    {plan.ctaLabel}
                  </a>
                  <a
                    href={plan.secondaryHref}
                    target={plan.secondaryHref.startsWith('http') ? '_blank' : undefined}
                    rel={plan.secondaryHref.startsWith('http') ? 'noopener noreferrer' : undefined}
                    className="block w-full text-center px-6 py-3 rounded-full text-sm font-semibold text-foreground-600 hover:text-foreground-900 hover:bg-background-100 transition-all duration-300 whitespace-nowrap cursor-pointer"
                  >
                    {plan.secondaryLabel}
                    <i className="ri-arrow-right-up-line ml-1.5 text-xs"></i>
                  </a>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Premium Notice Box */}
        <div className="max-w-5xl mx-auto mb-16 md:mb-20">
          <div className="relative bg-gradient-to-br from-background-50 via-background-50 to-primary-50/40 border border-background-200/70 rounded-[20px] p-8 md:p-10 overflow-hidden group hover:border-primary-200/50 transition-all duration-500">
            {/* Subtle glow */}
            <div className="absolute -top-20 -right-20 w-60 h-60 rounded-full bg-primary-500/5 blur-3xl pointer-events-none"></div>
            <div className="absolute -bottom-20 -left-20 w-60 h-60 rounded-full bg-primary-400/5 blur-3xl pointer-events-none"></div>

            <div className="relative z-10 flex flex-col lg:flex-row items-center gap-8">
              {/* Text Content */}
              <div className="flex-1 text-center lg:text-left">
                <h3 className="text-xl md:text-2xl font-bold font-heading text-foreground-950 mb-3">
                  Need More Features?
                </h3>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-3">
                  Looking for advanced automation, AI Chatbot, CRM Integration, Multi-Agent Support or Enterprise Solutions?
                </p>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-50 border border-amber-200/60 text-amber-700 text-xs font-semibold mr-1.5">
                    <i className="ri-sparkling-line text-xs"></i> Enterprise Plan
                  </span>
                  are available on request. Contact our experts for a customized solution tailored to your business.
                </p>
              </div>

              {/* CTA */}
              <div className="flex-shrink-0">
                <a
                  href="https://wa.me/919746765488?text=Hi,%0A%0AI%20am%20interested%20in%20your%20Pro%20Plan.%20Please%20share%20more%20details."
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group inline-flex items-center gap-2 px-8 py-4 rounded-full text-sm font-bold bg-gradient-to-r from-secondary-900 to-secondary-800 text-white hover:from-secondary-800 hover:to-secondary-700 transition-all duration-300 hover:scale-[1.03] active:scale-[0.98] whitespace-nowrap cursor-pointer shadow-[0_4px_20px_rgba(0,0,0,0.12)]"
                >
                  Request Enterprise Plan
                  <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform"></i>
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Trust Section */}
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <p className="text-sm font-semibold text-foreground-400 uppercase tracking-wider">
              Why businesses trust Digi Flow
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-5">
            {trustItems.map(item => (
              <div
                key={item.title}
                className="group text-center p-6 rounded-[20px] bg-background-50 border border-background-200/70 hover:border-background-300/70 transition-all duration-400 hover:-translate-y-1"
              >
                <div className="w-12 h-12 rounded-2xl bg-primary-50 flex items-center justify-center mx-auto mb-4 group-hover:bg-primary-100 transition-colors duration-300">
                  <i className={`${item.icon} text-xl text-primary-500`}></i>
                </div>
                <h4 className="text-sm font-bold font-heading text-foreground-950 mb-1.5">
                  {item.title}
                </h4>
                <p className="text-xs text-foreground-500 leading-relaxed">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}